import type {
  ParsedFileData,
  DocumentTypeConfig,
  ExtractedFileData,
  ExtractedField,
  ExtractionError,
  ExtractionWarning,
  CellValueType,
} from "./types";
import { findSheetByNameOrIndex, extractFieldsFromPdfText } from "./parsers";

type ExtractionResult = {
  extracted: ExtractedFileData;
  errors: ExtractionError[];
  warnings: ExtractionWarning[];
};

export function extractFieldsFromFile(
  parsedFile: ParsedFileData,
  config: DocumentTypeConfig
): ExtractionResult {
  const errors: ExtractionError[] = [];
  const warnings: ExtractionWarning[] = [];
  const fields: Record<string, ExtractedField> = {};

  if (
    parsedFile.fileFormat === "pdf" &&
    config.documentType === "notification_decision"
  ) {
    const pdfSheet = findSheetByNameOrIndex(parsedFile, "_pdf_text");
    const pdfText = pdfSheet?.cells["A1"]?.rawValue;

    if (typeof pdfText === "string" && pdfText.length > 0) {
      const pdfResult = extractFieldsFromPdfText(pdfText, parsedFile.fileName);
      Object.assign(fields, pdfResult.fields);
      errors.push(...pdfResult.errors);
      warnings.push(...pdfResult.warnings);
    } else {
      errors.push({
        file: parsedFile.fileName,
        code: "PARSE_FAILED",
        message: "No text content extracted from PDF",
      });
    }
  } else if (parsedFile.fileFormat === "xlsx" && config.xlsx) {
    for (const sheetConfig of config.xlsx.sheets) {
      const sheet = findSheetByNameOrIndex(
        parsedFile,
        sheetConfig.sheetName,
        sheetConfig.sheetIndex
      );

      if (!sheet) {
        errors.push({
          file: parsedFile.fileName,
          code: "SHEET_NOT_FOUND",
          message: `Sheet "${sheetConfig.sheetName}" not found in file`,
        });
        continue;
      }

      for (const mapping of sheetConfig.mappings) {
        const cellData = sheet.cells[mapping.cell];
        const isEmpty =
          !cellData ||
          cellData.rawValue === undefined ||
          cellData.rawValue === null ||
          cellData.rawValue === "";

        if (isEmpty) {
          if (mapping.required) {
            errors.push({
              file: parsedFile.fileName,
              field: mapping.field,
              code: "MISSING_REQUIRED_FIELD",
              message: `Required field "${mapping.field}" at cell ${mapping.cell} is empty`,
            });
          } else {
            warnings.push({
              file: parsedFile.fileName,
              field: mapping.field,
              code: "EMPTY_VALUE",
              message: `Optional field "${mapping.field}" at cell ${mapping.cell} is empty`,
            });
          }

          if (mapping.defaultValue !== undefined) {
            fields[mapping.field] = {
              field: mapping.field,
              rawValue: null,
              parsedValue: mapping.defaultValue,
              cell: mapping.cell,
              sheetName: sheet.sheetName,
            };
          }
          continue;
        }

        const coercedValue = coerceValue(
          cellData.rawValue,
          mapping.valueType,
          warnings,
          parsedFile.fileName,
          mapping.field
        );
        const parsedValue =
          mapping.stripLabelPrefix && typeof coercedValue === "string"
            ? coercedValue.replace(/^[^:]+:\s*/s, "").trim()
            : coercedValue;
        const finalValue = mapping.transform
          ? mapping.transform(parsedValue)
          : parsedValue;

        fields[mapping.field] = {
          field: mapping.field,
          rawValue: cellData.rawValue,
          parsedValue: finalValue,
          cell: mapping.cell,
          sheetName: sheet.sheetName,
        };
      }
    }
  }

  return {
    extracted: {
      fileName: parsedFile.fileName,
      documentType: config.documentType,
      version: null,
      fields,
    },
    errors,
    warnings,
  };
}

function coerceValue(
  raw: unknown,
  targetType: CellValueType,
  warnings: ExtractionWarning[],
  fileName: string,
  field: string
): unknown {
  switch (targetType) {
    case "string":
      return String(raw ?? "");
    case "number": {
      const num = Number(raw);
      if (Number.isNaN(num)) {
        warnings.push({
          file: fileName,
          field,
          code: "TYPE_COERCION",
          message: `Could not parse "${raw}" as number for field "${field}"`,
        });
        return 0;
      }
      return num;
    }
    case "boolean": {
      if (typeof raw === "boolean") return raw;
      if (typeof raw === "string") {
        const lower = raw.toLowerCase().trim();
        return (
          lower === "da" || lower === "true" || lower === "1" || lower === "yes"
        );
      }
      return Boolean(raw);
    }
    case "date":
      return String(raw);
    default:
      return raw;
  }
}
